import axios from 'axios';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

// Refresh 2 minutes before the access token actually expires.
const REFRESH_SAFETY_MARGIN_MS = 2 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 5000;

// Dedicated instance (no interceptors) so the refresh call never recurses
// through the global auth interceptors installed on the default axios export.
// Exported (only) so tests can spy on it directly — axios binds an
// instance's get/post/etc. to the prototype method that existed at creation
// time, so spying on axios.Axios.prototype after the fact never intercepts
// calls made through an already-created instance like this one.
export const refreshClient = axios.create();

let refreshTimer = null;

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const setTokens = ({ accessToken, refreshToken } = {}) => {
  if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const getAccessTokenExp = () => {
  const token = getAccessToken();
  if (!token) return null;

  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) return null;

    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    const payload = JSON.parse(json);
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch (error) {
    return null;
  }
};

export const refreshAccessToken = async (backendUrl) => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearTokens();
    throw new Error('No refresh token available');
  }

  try {
    const response = await refreshClient.post(`${backendUrl}/auth/refresh`, { refreshToken });
    setTokens(response.data);
    return response.data.accessToken;
  } catch (error) {
    clearTokens();
    throw error;
  }
};

export const cancelProactiveRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

export const scheduleProactiveRefresh = (backendUrl) => {
  cancelProactiveRefresh();

  const exp = getAccessTokenExp();
  if (!exp) return;

  const delay = Math.max(exp - Date.now() - REFRESH_SAFETY_MARGIN_MS, MIN_REFRESH_DELAY_MS);

  refreshTimer = setTimeout(async () => {
    try {
      await refreshAccessToken(backendUrl);
      scheduleProactiveRefresh(backendUrl);
    } catch (error) {
      // Log only the message — the full axios error serializes config.data,
      // which contains the refresh token.
      console.error('Proactive token refresh failed:', error?.message || error);
    }
  }, delay);
};
